import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dealService } from '../services';
import { DealReceiptInfoDTO, DealStatus } from '../types';

type ReceiptStatus = 'success' | 'fail' | 'unknown';

const ReceiptPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [receiptInfo, setReceiptInfo] = useState<DealReceiptInfoDTO | null>(null);
  const [actionLoading, setActionLoading] = useState<'confirm' | 'cancel' | null>(null);
  const [actionDone, setActionDone] = useState<'confirmed' | 'cancelled' | null>(null);
  const emailSentForPaidRef = useRef(false);

  const dealId = searchParams.get('id') ?? '';
  const token = searchParams.get('token') ?? '';
  const statusParam = (searchParams.get('status') ?? '').toLowerCase();

  const status: ReceiptStatus =
    statusParam === 'success' ? 'success' : statusParam === 'fail' ? 'fail' : 'unknown';

  const effectiveToken = (receiptInfo?.payerConfirmToken || token) || '';

  useEffect(() => {
    if (!dealId || status !== 'success') return;
    const fetchReceipt = () => dealService.getDealReceiptInfo(dealId, token || undefined).then(setReceiptInfo);
    fetchReceipt();
    const t = setInterval(fetchReceipt, 4000);
    return () => clearInterval(t);
  }, [dealId, token, status]);

  useEffect(() => {
    if (!dealId || status !== 'success') return;
    dealService.ensurePayerEmailSent(dealId);
  }, [dealId, status]);

  useEffect(() => {
    if (receiptInfo?.status === DealStatus.Paid && !emailSentForPaidRef.current) {
      emailSentForPaidRef.current = true;
      dealService.ensurePayerEmailSent(dealId);
    }
  }, [receiptInfo?.status, dealId]);

  const actionFromUrl = searchParams.get('action');
  useEffect(() => {
    if (actionDone || !receiptInfo || !effectiveToken || !actionFromUrl) return;
    if (actionFromUrl === 'confirm' && receiptInfo.canConfirm) {
      setActionLoading('confirm');
      dealService.payerConfirmDeal(dealId, effectiveToken).then((result) => {
        setActionLoading(null);
        if (result) setActionDone('confirmed');
      });
    } else if (actionFromUrl === 'cancel' && receiptInfo.canCancel) {
      setActionLoading('cancel');
      dealService.payerRejectDeal(dealId, effectiveToken).then((result) => {
        setActionLoading(null);
        if (result) setActionDone('cancelled');
      });
    }
  }, [receiptInfo, effectiveToken, actionFromUrl, dealId, actionDone]);

  const ui = useMemo(() => {
    switch (status) {
      case 'success':
        return {
          title: 'Оплата прошла успешно',
          description: 'Платёж принят банком. Спасибо!',
          details:
            'Подтвердите или отмените сделку в течение 3 дней. Если вы не подтвердите и не отмените сделку, она будет считаться подтверждённой автоматически.',
        };
      case 'fail':
        return {
          title: 'Оплата не выполнена',
          description: 'Платёж не прошёл или был отменён.',
          details:
            'Если хотите повторить оплату, попросите у продавца новую ссылку. При необходимости обратитесь в поддержку.',
        };
      default:
        return {
          title: 'Статус оплаты',
          description: 'Не удалось определить статус платежа по параметрам редиректа.',
          details: 'Проверьте ссылку или вернитесь к продавцу для уточнения статуса оплаты.',
        };
    }
  }, [status]);

  const Icon = () => {
    if (status === 'success') {
      return (
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center border border-emerald-500/30">
          <svg className="h-7 w-7 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }

    if (status === 'fail') {
      return (
        <div className="mx-auto h-14 w-14 rounded-full bg-rose-500/15 flex items-center justify-center border border-rose-500/30">
          <svg className="h-7 w-7 text-rose-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }

    return (
      <div className="mx-auto h-14 w-14 rounded-full bg-white/10 flex items-center justify-center border border-white/10">
        <svg className="h-7 w-7 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9z"
          />
        </svg>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#141826] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
        <div className="text-center">
          <Icon />

          <h1 className="mt-4 text-2xl font-semibold text-white">{ui.title}</h1>
          <p className="mt-2 text-sm text-white/70">{ui.description}</p>
          <p className="mt-3 text-sm text-white/60">{ui.details}</p>

          {status === 'success' && receiptInfo?.status === DealStatus.Paid && receiptInfo.canConfirm && receiptInfo.canCancel && effectiveToken && !actionDone && (
            <div className="mt-5 flex flex-col gap-3">
              <p className="text-sm text-white/70">Подтвердите или отмените сделку:</p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (!effectiveToken) return;
                    setActionLoading('confirm');
                    const result = await dealService.payerConfirmDeal(dealId, effectiveToken);
                    setActionLoading(null);
                    if (result) setActionDone('confirmed');
                  }}
                  disabled={!!actionLoading}
                  className="flex-1 inline-flex items-center justify-center px-4 py-3 text-sm font-medium rounded-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {actionLoading === 'confirm' ? '...' : 'Подтвердить сделку'}
                </button>
                <button
                  onClick={async () => {
                    if (!effectiveToken) return;
                    setActionLoading('cancel');
                    const result = await dealService.payerRejectDeal(dealId, effectiveToken);
                    setActionLoading(null);
                    if (result) setActionDone('cancelled');
                  }}
                  disabled={!!actionLoading}
                  className="flex-1 inline-flex items-center justify-center px-4 py-3 text-sm font-medium rounded-full bg-slate-600 text-white hover:bg-slate-500 disabled:opacity-50"
                >
                  {actionLoading === 'cancel' ? '...' : 'Отменить сделку'}
                </button>
              </div>
              <p className="text-xs text-white/40 mt-1">
                Письмо с ссылками также отправлено на вашу почту. Если не видите письмо — проверьте папку «Спам».
              </p>
            </div>
          )}

          {status === 'success' && receiptInfo?.status === DealStatus.Wait && (
            <p className="mt-4 text-sm text-white/50">
              Ожидаем подтверждения оплаты банком. Обновите страницу через несколько секунд — появятся кнопки для подтверждения или отмены сделки. Письмо с ссылками придёт на вашу почту.
            </p>
          )}

          {status === 'success' && receiptInfo?.status === DealStatus.Paid && !effectiveToken && !actionDone && (
            <p className="mt-4 text-sm text-white/50">
              Обновите страницу — должны появиться кнопки. Письмо с ссылками отправлено на вашу почту (проверьте папку «Спам»).
            </p>
          )}

          {actionDone === 'confirmed' && (
            <p className="mt-4 text-sm text-emerald-400">Сделка подтверждена. Ожидайте подтверждения получателя.</p>
          )}
          {actionDone === 'cancelled' && (
            <p className="mt-4 text-sm text-amber-400">Сделка отменена. Деньги будут возвращены на вашу карту.</p>
          )}

          {dealId && (
            <div className="mt-5 text-xs text-white/50 break-all">
              ID сделки: <span className="text-white/70">{dealId}</span>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-full text-gray-800 bg-white hover:bg-white/90 transition-colors"
            >
              Перейти в личный кабинет
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-full text-white border border-white/15 hover:bg-white/5 transition-colors"
            >
              Войти
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPage;

